import { Shield, ArrowUpRight } from "lucide-react";
import { products } from "@/lib/products";
import { PRODUCTS_BUY_URL } from "@/lib/translations";

const IMMUNE_SLUGS = ["immunocal-azul", "immunocal-platinum", "immunocal-sport"];

const FUNCTIONS = {
  es: {
    "immunocal-azul":
      "Precursor de glutatión — el antioxidante maestro que activa y fortalece tu sistema inmunológico de adentro hacia afuera.",
    "immunocal-platinum":
      "Inmunidad avanzada para adultos 35+, regula la inflamación crónica y equilibra el pH del cuerpo.",
    "immunocal-sport":
      "Resiliencia inmune para personas activas: combina protección inmunológica con mejor oxigenación, fuerza y recuperación.",
  },
  en: {
    "immunocal-azul":
      "Glutathione precursor — the master antioxidant that activates and strengthens your immune system from the inside out.",
    "immunocal-platinum":
      "Advanced immunity for adults 35+, modulates chronic inflammation and balances the body's pH.",
    "immunocal-sport":
      "Immune resilience for active people: combines immune protection with better oxygenation, strength and recovery.",
  },
};

export default function ImmuneShieldSection({ language }) {
  const immuneProducts = IMMUNE_SLUGS.map((s) =>
    products.find((p) => p.slug === s)
  ).filter(Boolean);

  return (
    <section
      id="immune-shield"
      data-testid="immune-shield-section"
      className="py-24 md:py-32 bg-[#1a2421] text-[#f9f6f0] relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, #bc6c25 0%, transparent 40%), radial-gradient(circle at 80% 70%, #2c4a3b 0%, transparent 50%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-[#bc6c25]/15 text-[#f0c79f] rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.22em]">
            <Shield className="w-3.5 h-3.5" />
            {language === "es" ? "Inmunidad inteligente" : "Smart immunity"}
          </div>
          <h2 className="font-serif font-light tracking-tight leading-[0.95] text-4xl md:text-6xl mt-6">
            {language === "es"
              ? "Siempre preparado. Siempre funcional."
              : "Always ready. Always functional."}
          </h2>
          <p className="mt-6 text-lg md:text-xl leading-relaxed text-[#f9f6f0]/80">
            {language === "es" ? (
              <>
                Un sistema inmunológico fuerte, inteligente, alerta y resiliente,{" "}
                <span className="text-[#bc6c25] font-semibold">SÍ ES POSIBLE.</span>
                <br />
                Manténte listo para enfrentar virus, bacterias y pandemias.
              </>
            ) : (
              <>
                A strong, smart, alert and resilient immune system{" "}
                <span className="text-[#bc6c25] font-semibold">IS POSSIBLE.</span>
                <br />
                Stay ready to face viruses, bacteria and pandemics.
              </>
            )}
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {immuneProducts.map((p) => (
            <article
              key={p.slug}
              data-testid={`immune-card-${p.slug}`}
              className="group bg-[#f9f6f0] text-[#1a2421] rounded-3xl overflow-hidden flex flex-col transition-all hover:-translate-y-1 hover:shadow-[0_30px_60px_-20px_rgba(188,108,37,0.4)]"
            >
              <div className="aspect-square bg-white relative overflow-hidden">
                <img
                  src={p.officialImage || p.image}
                  alt={p.name[language]}
                  className="absolute inset-0 w-full h-full object-contain p-8 transition-opacity duration-500 group-hover:opacity-0"
                  loading="lazy"
                />
                <img
                  src={`/products/immune/${p.slug}.png`}
                  alt=""
                  aria-hidden
                  className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                  loading="lazy"
                />
              </div>
              <div className="p-7 flex flex-col flex-1">
                <h3 className="font-serif text-2xl text-[#1a2421] leading-snug">
                  {p.name[language]}
                </h3>
                <p className="mt-3 text-sm text-[#5c6b61] leading-relaxed flex-1">
                  {FUNCTIONS[language][p.slug]}
                </p>
                <a
                  href={PRODUCTS_BUY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`immune-buy-${p.slug}`}
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#bc6c25] hover:bg-[#9c581e] text-white px-6 py-3 text-sm font-bold transition-colors"
                >
                  {language === "es" ? "Comprar ahora" : "Buy now"}
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
